import { makeAuthedRequest } from "utils/makeAuthedRequest";
import { useQuery, useMutation, useQueryClient } from "react-query";

export function useCreditCards() {
  const queryClient = useQueryClient();

  const { data, status, isLoading, refetch } = useQuery(
    ["credit-cards"],
    () =>
      makeAuthedRequest("v1/settings/card", {
        method: "GET",
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const deleteCard = useMutation(
    (membershipCreditCardId) =>
      makeAuthedRequest(`v1/settings/card/${membershipCreditCardId}`, {
        method: "DELETE",
      }).then(async (res) => {
        if (res.ok) {
          return res;
        } else {
          const body = await res.json();
          throw new Error(body.error);
        }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("credit-cards");
      },
      onError: (err) => {
        console.error(err);
      },
    }
  );

  const isIgnorableError = (err) => {
    return (
      err?.name === "AbortError" ||
      (err instanceof TypeError && err.message?.includes("NetworkError"))
    );
  };

  const updateCard = useMutation(
    async (cardData) => {
      const res = await makeAuthedRequest(`v1/settings/card/update-card`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cardData),
      });

      if (res.ok) {
        const responseText = await res.text();
        return responseText ? JSON.parse(responseText) : {};
      } else {
        const responseText = await res.text();
        const body = responseText ? JSON.parse(responseText) : {};
        throw new Error(body.message || "Unknown error");
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("credit-cards");
      },
      onError: (err) => {
        if (isIgnorableError(err)) return; // 👈 ignore canceled requests

        console.error("Update card error:", err.message);
        console.error("Error details:", err);
      },
    }
  );

  const addCustomer = useMutation(
    async ({ paymentToken, postalCode }) => {
      const res = await makeAuthedRequest(`v1/settings/card/add-customer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ paymentToken, postalCode }),
      });

      if (res.ok) {
        return await res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message || "Failed to add customer");
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("credit-cards");
      },
      onError: (err) => {
        console.error("Add customer error:", err.message);
        console.error("Error details:", err);
      },
    }
  );

  const addCard = useMutation(
    async (cardData) => {
      const res = await makeAuthedRequest(`v1/settings/card/add-card`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cardData),
      });

      if (res.ok) {
        return await res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message || "Failed to add card");
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("credit-cards");
        queryClient.invalidateQueries("issues");
      },
      onError: (err) => {
        console.error("Add card error:", err.message);
        console.error("Error details:", err);
      },
    }
  );

  return {
    data,
    status,
    isLoading,
    refetch,
    deleteCard,
    updateCard,
    addCustomer,
    addCard,
  };
}
